/** Insight article pages — dummy content for /insights/:slug */

import { pageRoute, appRoute } from "./paths.js";

function articleSections(topic) {
  return [
    {
      heading: "The short version",
      body: `${topic} usually fails when teams optimize for activity instead of outcomes. Start with the decision you need a user or system to make, then design the smallest path that makes that decision obvious.`,
    },
    {
      heading: "What good looks like",
      body: "Clear ownership, measurable signals, and a delivery cadence that survives contact with reality. Dummy copy for now — replace with your real editorial voice when publishing.",
    },
    {
      heading: "A practical next move",
      body: "Pick one workflow, instrument it, and ship a thin slice this week. If it doesn’t change a metric or a team’s confidence, it wasn’t the right slice.",
    },
  ];
}

/**
 * @typedef {object} InsightDetail
 * @property {string} slug
 * @property {string} title
 * @property {string} category
 * @property {string} readTime
 * @property {string} excerpt
 * @property {string} serviceId
 * @property {string} serviceLabel
 * @property {string} eyebrow
 * @property {string} lead
 * @property {string} pullQuote
 * @property {string[]} takeaways
 * @property {{ heading: string, body: string }[]} sections
 * @property {string[]} related
 * @property {string} accent
 */

/** @type {Record<string, Omit<InsightDetail, "slug">>} */
const details = {
  "why-design-systems-speed-up-web-squads": {
    title: "Why design systems speed up web squads",
    category: "Frontend",
    readTime: "4 min",
    excerpt: "How shared components and tokens cut rework across marketing, app, and admin surfaces.",
    serviceId: "web-development",
    serviceLabel: "Web Development",
    eyebrow: "Systems thinking",
    lead: "Shared tokens and components don’t slow creative teams — they remove the tax of reinventing the same button sixteen times.",
    pullQuote: "A design system is a delivery system dressed as UI.",
    takeaways: [
      "Tokens beat one-off hex values when multiple surfaces must stay coherent",
      "Document patterns, not just components",
      "Adoption tooling matters as much as the library itself",
    ],
    sections: articleSections("Design-system adoption"),
    related: ["shipping-saas-ui-in-weekly-cycles", "tokens-that-frontend-teams-actually-use"],
  },
  "shipping-saas-ui-in-weekly-cycles": {
    title: "Shipping SaaS UI in weekly cycles",
    category: "Delivery",
    readTime: "5 min",
    excerpt: "A practical cadence for reviews, accessibility checks, and deploy-ready pull requests.",
    serviceId: "web-development",
    serviceLabel: "Web Development",
    eyebrow: "Cadence",
    lead: "Weekly shipping isn’t about speed theater — it’s about a definition of done that teams can repeat without burning out.",
    pullQuote: "Cadence is a product feature for the org.",
    takeaways: [
      "Protect a fixed demo window",
      "Automate the boring quality gates",
      "Keep release notes human-readable",
    ],
    sections: articleSections("Weekly delivery"),
    related: ["why-design-systems-speed-up-web-squads", "when-to-choose-nextjs-vs-a-spa"],
  },
  "when-to-choose-nextjs-vs-a-spa": {
    title: "When to choose Next.js vs. a SPA",
    category: "Stack",
    readTime: "6 min",
    excerpt: "A simple framework for founders balancing SEO, app UX, and engineering complexity.",
    serviceId: "web-development",
    serviceLabel: "Web Development",
    eyebrow: "Architecture",
    lead: "Framework choice should follow distribution and product shape — not Twitter consensus from last quarter.",
    pullQuote: "Pick the boring option that matches how users find you.",
    takeaways: [
      "SEO-heavy acquisition leans toward SSR/SSG",
      "Authenticated product density can stay SPA-first",
      "Complexity tax compounds faster than you expect",
    ],
    sections: articleSections("Framework selection"),
    related: ["shipping-saas-ui-in-weekly-cycles", "why-design-systems-speed-up-web-squads"],
  },
  "scope-the-job-before-the-model": {
    title: "Scope the job before the model",
    category: "Agents",
    readTime: "4 min",
    excerpt: "Why clear workflow ownership beats vague “add an agent” initiatives.",
    serviceId: "ai-agentic",
    serviceLabel: "Agentic AI",
    eyebrow: "Agent strategy",
    lead: "Agents fail when the job is fuzzy. Name the workflow, the tools, and the handoff points before you debate models.",
    pullQuote: "Autonomy without a job description is just expensive improvisation.",
    takeaways: [
      "Define success metrics before tooling",
      "List irreversible actions early",
      "Design human escalation as a feature",
    ],
    sections: articleSections("Agent scoping"),
    related: ["evals-keep-agents-honest", "human-handoffs-that-users-trust"],
  },
  "evals-keep-agents-honest": {
    title: "Evals that keep agents honest",
    category: "Quality",
    readTime: "5 min",
    excerpt: "A lightweight loop for tool-calling quality, regressions, and cost control.",
    serviceId: "ai-agentic",
    serviceLabel: "Agentic AI",
    eyebrow: "Quality loops",
    lead: "If you can’t score an agent, you can’t safely upgrade it. Evals are the unit tests of autonomous systems.",
    pullQuote: "Trust is a measured property, not a vibe.",
    takeaways: [
      "Golden tasks beat toy prompts",
      "Track cost beside correctness",
      "Gate deploys on behavioral regressions",
    ],
    sections: articleSections("Agent evaluation"),
    related: ["scope-the-job-before-the-model", "human-handoffs-that-users-trust"],
  },
  "human-handoffs-that-users-trust": {
    title: "Human handoffs that users trust",
    category: "Product",
    readTime: "5 min",
    excerpt: "Design patterns for when agents should ask, escalate, or stop.",
    serviceId: "ai-agentic",
    serviceLabel: "Agentic AI",
    eyebrow: "Collaborative UX",
    lead: "The best agent products feel supervised. Handoffs should feel calm, explicit, and easy to resume.",
    pullQuote: "Uncertainty is fine — silence is not.",
    takeaways: [
      "Summarize context for humans",
      "Offer approve / edit / reject controls",
      "Measure resume success after escalation",
    ],
    sections: articleSections("Agent handoffs"),
    related: ["scope-the-job-before-the-model", "evals-keep-agents-honest"],
  },
  "mvp-means-retention-not-features": {
    title: "MVP means retention, not features",
    category: "Apps",
    readTime: "4 min",
    excerpt: "How to pick the smallest product that still proves people come back.",
    serviceId: "app-development",
    serviceLabel: "App Development",
    eyebrow: "Product strategy",
    lead: "An MVP that nobody returns to isn’t minimal — it’s incomplete. Aim for the smallest loop that creates habit.",
    pullQuote: "Retention is the only MVP metric that survives demo day.",
    takeaways: [
      "Define the aha moment first",
      "Cut features that don’t protect that moment",
      "Instrument return behavior from week one",
    ],
    sections: articleSections("App MVP design"),
    related: ["tenancy-decisions-you-shouldnt-defer", "billing-ux-that-doesnt-kill-conversion"],
  },
  "tenancy-decisions-you-shouldnt-defer": {
    title: "Tenancy decisions you shouldn’t defer",
    category: "Architecture",
    readTime: "6 min",
    excerpt: "What to lock early so growth doesn’t force a rewrite.",
    serviceId: "app-development",
    serviceLabel: "App Development",
    eyebrow: "Platform",
    lead: "Tenancy is expensive to retrofit. Choose workspace boundaries before your first paying customer hard-codes assumptions.",
    pullQuote: "Isolation debt compounds quietly — then loudly.",
    takeaways: [
      "Model workspaces before billing edge cases",
      "Enforce tenant scope at the data layer",
      "Plan support impersonation with policy",
    ],
    sections: articleSections("Multi-tenant architecture"),
    related: ["mvp-means-retention-not-features", "billing-ux-that-doesnt-kill-conversion"],
  },
  "billing-ux-that-doesnt-kill-conversion": {
    title: "Billing UX that doesn’t kill conversion",
    category: "Growth",
    readTime: "5 min",
    excerpt: "Plan, trial, and upgrade patterns that feel product-native.",
    serviceId: "app-development",
    serviceLabel: "App Development",
    eyebrow: "Monetization",
    lead: "Billing should feel like a product surface — clear entitlements, calm dunning, and upgrades that don’t interrupt flow.",
    pullQuote: "If finance can’t reconcile it, customers won’t trust it.",
    takeaways: [
      "Entitlements should unlock instantly",
      "Failed payments need guided recovery",
      "Plan changes must read as intentional, not punitive",
    ],
    sections: articleSections("Billing experience"),
    related: ["mvp-means-retention-not-features", "tenancy-decisions-you-shouldnt-defer"],
  },
  "design-the-decision-not-the-screen": {
    title: "Design the decision, not the screen",
    category: "UX",
    readTime: "4 min",
    excerpt: "Why job-centered flows beat page inventories every time.",
    serviceId: "ui-ux-design",
    serviceLabel: "UI/UX Design",
    eyebrow: "UX craft",
    lead: "Screens are containers. The real design problem is the decision the user must make next — and how little friction that takes.",
    pullQuote: "If everything is primary, nothing is.",
    takeaways: [
      "One primary action per view",
      "Map decisions before wireframes",
      "Test comprehension, not aesthetics alone",
    ],
    sections: articleSections("Decision-centered design"),
    related: ["tokens-that-frontend-teams-actually-use", "lightweight-tests-before-big-redesigns"],
  },
  "tokens-that-frontend-teams-actually-use": {
    title: "Tokens that frontend teams actually use",
    category: "Systems",
    readTime: "5 min",
    excerpt: "Naming and structure that reduce “close enough” implementation drift.",
    serviceId: "ui-ux-design",
    serviceLabel: "UI/UX Design",
    eyebrow: "Design systems",
    lead: "Semantic tokens win when engineers can guess the name without opening Figma. Purpose beats palette order.",
    pullQuote: "A token nobody adopts is just documentation cosplay.",
    takeaways: [
      "Name by role, not by hue",
      "Export the same source to design and code",
      "Lint against raw values in PRs",
    ],
    sections: articleSections("Token architecture"),
    related: ["design-the-decision-not-the-screen", "why-design-systems-speed-up-web-squads"],
  },
  "lightweight-tests-before-big-redesigns": {
    title: "Lightweight tests before big redesigns",
    category: "Research",
    readTime: "5 min",
    excerpt: "A practical research cadence for startups without a research org.",
    serviceId: "ui-ux-design",
    serviceLabel: "UI/UX Design",
    eyebrow: "Research",
    lead: "You don’t need a research department to reduce redesign risk — you need a weekly habit of talking to users.",
    pullQuote: "Five conversations beat fifty opinions.",
    takeaways: [
      "Schedule recurring lightweight tests",
      "Capture friction in ranked lists",
      "Kill weak ideas before engineering starts",
    ],
    sections: articleSections("Lean research"),
    related: ["design-the-decision-not-the-screen", "tokens-that-frontend-teams-actually-use"],
  },
  "threat-models-before-tools": {
    title: "Threat models before tools",
    category: "Security",
    readTime: "4 min",
    excerpt: "A simple filter for what to harden first — before buying another dashboard.",
    serviceId: "cyber-security",
    serviceLabel: "Cyber Security",
    eyebrow: "Strategy",
    lead: "Most security programs stall on tooling. Start with the decision an attacker wants, then pick the smallest control that makes that decision expensive.",
    pullQuote: "A dashboard without a threat model is just another inbox.",
    takeaways: [
      "Name the assets and identities that actually matter",
      "Rank likely abuse paths before shopping for platforms",
      "Buy tools only to cover a mapped gap",
    ],
    sections: articleSections("Threat modeling"),
    related: ["identity-is-the-new-perimeter", "ship-security-into-the-sprint"],
  },
  "identity-is-the-new-perimeter": {
    title: "Identity is the new perimeter",
    category: "Identity",
    readTime: "5 min",
    excerpt: "SSO, least privilege, and session design that survive a real incident.",
    serviceId: "cyber-security",
    serviceLabel: "Cyber Security",
    eyebrow: "Access",
    lead: "Network edges fade. Sessions, tokens, and roles are where incidents actually start — and where containment has to work.",
    pullQuote: "If identity is messy, every other control is theater.",
    takeaways: [
      "SSO and MFA before more network rules",
      "Least privilege as a product decision, not a spreadsheet",
      "Make session expiry and recovery boring and explicit",
    ],
    sections: articleSections("Identity hardening"),
    related: ["threat-models-before-tools", "ship-security-into-the-sprint"],
  },
  "ship-security-into-the-sprint": {
    title: "Ship security into the sprint",
    category: "Delivery",
    readTime: "6 min",
    excerpt: "How to make findings a backlog item, not a once-a-year fire drill.",
    serviceId: "cyber-security",
    serviceLabel: "Cyber Security",
    eyebrow: "Delivery",
    lead: "Annual audits surprise teams because findings never had an owner in the sprint. Treat security work like product work — sized, scheduled, and reviewed.",
    pullQuote: "If it isn’t in the sprint, it isn’t a control. It’s a wish.",
    takeaways: [
      "Turn findings into tickets with owners",
      "Budget remediation, not just the assessment",
      "Review detection the same way you review features",
    ],
    sections: articleSections("Secure delivery"),
    related: ["threat-models-before-tools", "identity-is-the-new-perimeter"],
  },
  "from-one-engineer-to-a-full-pod": {
    title: "From one engineer to a full pod",
    category: "Teams",
    readTime: "5 min",
    excerpt: "A practical path for growing capacity without rebuilding recruiting.",
    serviceId: "staff-augmentation",
    serviceLabel: "Staff Augmentation",
    eyebrow: "Scaling teams",
    lead: "Start with one high-ownership engineer, prove the rhythm, then expand into a pod with clear roles.",
    pullQuote: "Capacity scales after trust scales.",
    takeaways: [
      "Validate fit before multiplying headcount",
      "Keep ownership boundaries explicit",
      "Grow roles around product needs, not titles",
    ],
    sections: articleSections("Team expansion"),
    related: ["make-timezone-overlap-non-negotiable", "interview-for-ownership-not-keywords"],
  },
  "make-timezone-overlap-non-negotiable": {
    title: "Make timezone overlap non-negotiable",
    category: "Delivery",
    readTime: "4 min",
    excerpt: "Why a few shared hours change everything for remote teammates.",
    serviceId: "staff-augmentation",
    serviceLabel: "Staff Augmentation",
    eyebrow: "Remote delivery",
    lead: "A few overlapping hours beat 24/7 chatter. Decisions happen in conversation, not overnight comment threads.",
    pullQuote: "Overlap is how remote teams stay one team.",
    takeaways: [
      "Book shared decision windows",
      "Async for updates, sync for choices",
      "Protect overlap as a delivery asset",
    ],
    sections: articleSections("Timezone collaboration"),
    related: ["from-one-engineer-to-a-full-pod", "interview-for-ownership-not-keywords"],
  },
  "interview-for-ownership-not-keywords": {
    title: "Interview for ownership, not keywords",
    category: "Leadership",
    readTime: "6 min",
    excerpt: "What high-performing embedded engineers show in week one.",
    serviceId: "staff-augmentation",
    serviceLabel: "Staff Augmentation",
    eyebrow: "Hiring signal",
    lead: "Keyword bingo misses the engineers who reduce ambiguity. Interview for judgment, communication, and follow-through.",
    pullQuote: "Week-one energy predicts quarter-one outcomes.",
    takeaways: [
      "Probe how candidates clarify incomplete specs",
      "Watch for proactive risk callouts",
      "Prefer ownership stories over buzzword density",
    ],
    sections: articleSections("Engineering interviews"),
    related: ["from-one-engineer-to-a-full-pod", "make-timezone-overlap-non-negotiable"],
  },
};

const INSIGHT_SLUG_ALIASES = {
  "use-chain-where-trust-is-the-product": "threat-models-before-tools",
  "wallet-ux-that-doesnt-scare-users": "identity-is-the-new-perimeter",
  "ship-ready-for-audit-not-surprised-by-it": "ship-security-into-the-sprint",
};

/* One accent per service, so every card filed under a service wears the same
   colour — picked to stay readable as tag text on the white cards. */
export const SERVICE_ACCENTS = {
  "ai-agentic": "#7c9a2e",
  "web-development": "#3b6fd6",
  "app-development": "#0e8fbf",
  "ui-ux-design": "#d64a7d",
  "cyber-security": "#c4841c",
  "staff-augmentation": "#2a9d6e",
};

const DEFAULT_ACCENT = "#3f5163";

export function insightAccent(serviceId) {
  return SERVICE_ACCENTS[serviceId] || DEFAULT_ACCENT;
}

export function getInsightDetail(slug) {
  const requested = String(slug || "").toLowerCase();
  const key = INSIGHT_SLUG_ALIASES[requested] || requested;
  const detail = details[key];
  if (!detail) return null;
  return { slug: key, ...detail, accent: insightAccent(detail.serviceId) };
}

export function getAllInsightDetails() {
  return Object.keys(details).map((slug) => getInsightDetail(slug));
}

export function insightDetailRoute(slug, section = "") {
  return pageRoute(`insights/${slug}`, section);
}

export function insightListFallbackHref() {
  return pageRoute("insights");
}

export function insightContactHref() {
  return appRoute("contact");
}

export function insightSignupHref() {
  return pageRoute("signup");
}
